import React, { useState, useEffect } from 'react';
import { 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical
} from 'lucide-react';

/**
 * Layout Editor
 * Edit flexbox and alignment properties
 */
export default function LayoutEditor({ element, onUpdate }) {
  const [display, setDisplay] = useState('flex');
  const [flexDirection, setFlexDirection] = useState('flex-row');
  const [justifyContent, setJustifyContent] = useState('justify-start');
  const [alignItems, setAlignItems] = useState('items-start');
  const [gap, setGap] = useState('gap-0');

  useEffect(() => {
    if (element?.element) {
      const classList = Array.from(element.element.classList);
      
      // Extract layout classes
      setDisplay(classList.find(c => ['flex', 'grid', 'block'].includes(c)) || 'flex');
      setFlexDirection(classList.find(c => c.match(/^flex-(row|col)/)) || 'flex-row');
      setJustifyContent(classList.find(c => c.match(/^justify-/)) || 'justify-start');
      setAlignItems(classList.find(c => c.match(/^items-/)) || 'items-start');
      setGap(classList.find(c => c.match(/^gap-/)) || 'gap-0');
    }
  }, [element]);

  const handleDisplayChange = (newDisplay) => {
    setDisplay(newDisplay);
    updateLayout('display', newDisplay);
  };

  const handleFlexDirectionChange = (newDirection) => {
    setFlexDirection(newDirection);
    updateLayout('flexDirection', newDirection);
  };

  const handleJustifyContentChange = (newJustify) => {
    setJustifyContent(newJustify);
    updateLayout('justifyContent', newJustify);
  };

  const handleAlignItemsChange = (newAlign) => {
    setAlignItems(newAlign);
    updateLayout('alignItems', newAlign);
  };

  const handleGapChange = (e) => {
    const newGap = e.target.value;
    setGap(newGap);
    updateLayout('gap', newGap);
  };

  const updateLayout = (property, value) => {
    if (!element) return;
    
    // Get style path from element or infer it
    const stylePath = element.element?.getAttribute('data-style-path') || 
                     inferStylePath(element.dataPath);
    
    if (!stylePath) {
      console.warn('No style path found for layout update');
      return;
    }
    
    // Update the specific style property
    const fullPath = `${stylePath}.${property}`;
    console.log(`🎛️ Updating layout: ${fullPath} → ${value}`);
    onUpdate(fullPath, value);
  };

  const inferStylePath = (dataPath) => {
    if (!dataPath) return null;
    const parts = dataPath.split('.');
    const lastPart = parts[parts.length - 1].replace(/\[\d+\]/, '');
    return `styles.${lastPart}`;
  };

  const gapOptions = [
    { value: 'gap-0', label: 'None (0px)' },
    { value: 'gap-1', label: 'XS (4px)' },
    { value: 'gap-2', label: 'SM (8px)' },
    { value: 'gap-3', label: 'MD (12px)' },
    { value: 'gap-4', label: 'LG (16px)' },
    { value: 'gap-6', label: 'XL (24px)' },
    { value: 'gap-8', label: '2XL (32px)' },
  ];

  return (
    <div className="space-y-4">
      {/* Display */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Display
        </label>
        <div className="flex gap-2">
          {['flex', 'grid', 'block'].map(d => (
            <button
              key={d}
              onClick={() => handleDisplayChange(d)}
              className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors capitalize ${
                display === d
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Flex Direction */}
      {display === 'flex' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Direction
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => handleFlexDirectionChange('flex-row')}
              className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors ${
                flexDirection === 'flex-row'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Row →
            </button>
            <button
              onClick={() => handleFlexDirectionChange('flex-col')}
              className={`flex-1 px-3 py-2 text-xs rounded-lg transition-colors ${
                flexDirection === 'flex-col'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Column ↓
            </button>
          </div>
        </div>
      )}

      {/* Justify Content (Main Axis) */}
      {display === 'flex' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Justify Content
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'justify-start', label: 'Start', icon: <AlignLeft className="w-4 h-4" /> },
              { value: 'justify-center', label: 'Center', icon: <AlignCenter className="w-4 h-4" /> },
              { value: 'justify-end', label: 'End', icon: <AlignRight className="w-4 h-4" /> },
              { value: 'justify-between', label: 'Between', icon: <AlignJustify className="w-4 h-4" /> },
              { value: 'justify-around', label: 'Around', icon: <AlignJustify className="w-4 h-4" /> },
              { value: 'justify-evenly', label: 'Evenly', icon: <AlignJustify className="w-4 h-4" /> },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleJustifyContentChange(option.value)}
                className={`px-2 py-2 text-xs rounded-lg transition-colors flex flex-col items-center gap-1 ${
                  justifyContent === option.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title={option.label}
              >
                {option.icon}
                <span className="text-xs">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Align Items (Cross Axis) */}
      {display === 'flex' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Align Items
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'items-start', label: 'Start', icon: <AlignStartVertical className="w-4 h-4" /> },
              { value: 'items-center', label: 'Center', icon: <AlignCenterVertical className="w-4 h-4" /> },
              { value: 'items-end', label: 'End', icon: <AlignEndVertical className="w-4 h-4" /> },
              { value: 'items-stretch', label: 'Stretch', icon: <AlignJustify className="w-4 h-4" /> },
              { value: 'items-baseline', label: 'Baseline', icon: <AlignJustify className="w-4 h-4" /> },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleAlignItemsChange(option.value)}
                className={`px-2 py-2 text-xs rounded-lg transition-colors flex flex-col items-center gap-1 ${
                  alignItems === option.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title={option.label}
              >
                {option.icon}
                <span className="text-xs">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gap */}
      {display === 'flex' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Gap
          </label>
          <select
            value={gap}
            onChange={handleGapChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {gapOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

